import { Box, Container, Link, Typography } from "@mui/material";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";

const colors = {
    cream: "#fffaf2",
    sand: "#f7f1e8",
    ink: "#2a2621",
    mutedLight: "rgba(255,255,255,0.72)",
    brown: "#9b7653",
    brownDark: "#5e422a",
    footer: "#2f2923",
};

const navLinks = [
    { label: "Aktuell", type: "section", target: "aktuelles" },
    { label: "Über uns", type: "section", target: "ueber-uns" },
    { label: "Sortiment", type: "section", target: "sortiment" },
    { label: "Mode", type: "section", target: "mode" },
    { label: "Tee", type: "section", target: "tee" },
    { label: "Kontakt", type: "section", target: "kontakt" },
    {
        label: "Shop",
        type: "external",
        href: "https://www.hooksieler-laedchen.de",
    },
];

const legalLinks = [
    { label: "Impressum", to: "/impressum" },
    { label: "Datenschutz", to: "/datenschutz" },
    { label: "AGB", to: "/agb" },
    { label: "Widerrufsrecht", to: "/widerrufsrecht" },
];

const paymentMethods = ["PayPal", "Vorkasse", "Rechnung"];

function FooterHeadline({ children }) {
    return (
        <Typography
            component="h3"
            sx={{
                mb: 2,
                fontFamily: '"Quicksand", sans-serif',
                fontSize: "1.35rem",
                lineHeight: 1.15,
                fontWeight: 700,
                color: "#fff",
                letterSpacing: "-0.03em",
            }}
        >
            {children}
        </Typography>
    );
}

function FooterText({ children }) {
    return (
        <Typography
            sx={{
                fontFamily: '"Nunito Sans", sans-serif',
                fontSize: 15,
                lineHeight: 1.7,
                color: colors.mutedLight,
            }}
        >
            {children}
        </Typography>
    );
}

function FooterBaseLink({ children, sx, ...props }) {
    return (
        <Link
            underline="none"
            sx={{
                display: "inline-flex",
                color: colors.mutedLight,
                fontFamily: '"Nunito Sans", sans-serif',
                fontSize: 15,
                lineHeight: 1.6,
                transition: "color 0.2s ease",

                "&:hover": {
                    color: "#fff",
                },

                ...sx,
            }}
            {...props}
        >
            {children}
        </Link>
    );
}

function Footer() {
    const navigate = useNavigate();
    const location = useLocation();

    function scrollToSection(sectionId) {
        const element = document.getElementById(sectionId);

        if (!element) return;

        element.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });
    }

    function handleSectionClick(event, sectionId) {
        event.preventDefault();

        if (location.pathname !== "/") {
            navigate("/");

            window.setTimeout(() => {
                scrollToSection(sectionId);
            }, 120);

            return;
        }

        scrollToSection(sectionId);
    }

    return (
        <Box
            component="footer"
            sx={{
                backgroundColor: colors.footer,
                color: "#fff",
                pt: {
                    xs: 7,
                    md: 9,
                },
            }}
        >
            <Container
                maxWidth={false}
                sx={{
                    width: "100%",
                    maxWidth: 1180,
                    mx: "auto",
                    px: {
                        xs: 2,
                        sm: 3,
                        md: 4,
                    },
                }}
            >
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            sm: "1fr 1fr",
                            lg: "1.15fr 0.85fr 1fr 1.2fr",
                        },
                        gap: {
                            xs: 4,
                            md: 5,
                        },
                        pb: {
                            xs: 5,
                            md: 7,
                        },
                    }}
                >
                    <Box>
                        <FooterHeadline>Hooksieler Lädchen</FooterHeadline>

                        <FooterText>
                            Claudia Don
                            <br />
                            Lange Straße 37
                            <br />
                            26434 Hooksiel
                        </FooterText>

                        <Box sx={{ mt: 2 }}>
                            <FooterText>
                                Telefon:{" "}
                                <FooterBaseLink href="tel:+494425353">
                                    04425 – 353
                                </FooterBaseLink>
                                <br />
                                Fax: 04425 – 6759821
                                <br />
                                E-Mail:{" "}
                                <FooterBaseLink href="mailto:info@hooksieler-laedchen.de">
                                    info@hooksieler-laedchen.de
                                </FooterBaseLink>
                            </FooterText>
                        </Box>
                    </Box>

                    <Box>
                        <FooterHeadline>Navigation</FooterHeadline>

                        <Box
                            component="nav"
                            sx={{
                                display: "grid",
                                gap: 0.5,
                            }}
                        >
                            {navLinks.map((item) => {
                                if (item.type === "external") {
                                    return (
                                        <FooterBaseLink
                                            key={item.label}
                                            href={item.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {item.label}
                                        </FooterBaseLink>
                                    );
                                }

                                return (
                                    <FooterBaseLink
                                        key={item.label}
                                        href={`/#${item.target}`}
                                        onClick={(event) =>
                                            handleSectionClick(event, item.target)
                                        }
                                    >
                                        {item.label}
                                    </FooterBaseLink>
                                );
                            })}
                        </Box>
                    </Box>

                    <Box>
                        <FooterHeadline>Rechtliches</FooterHeadline>

                        <Box
                            component="nav"
                            sx={{
                                display: "grid",
                                gap: 0.5,
                            }}
                        >
                            {legalLinks.map((item) => (
                                <FooterBaseLink
                                    key={item.label}
                                    component={RouterLink}
                                    to={item.to}
                                >
                                    {item.label}
                                </FooterBaseLink>
                            ))}
                        </Box>

                    </Box>

                    <Box>
                        <FooterHeadline>Zahlung & Versand</FooterHeadline>

                        <FooterText>
                            Lieferungen erfolgen innerhalb Deutschlands ab einem
                            Mindestbestellwert von 20,00 € zuzüglich Versand- und
                            Verpackungskosten in Höhe von 4,99 €.
                        </FooterText>

                        <Box sx={{ mt: 1.5 }}>
                            <FooterText>
                                Ab einem Bestellwert von 100,00 € liefern wir
                                versandkostenfrei.
                            </FooterText>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1,
                                mt: 2.2,
                                mb: 2.5,
                            }}
                        >
                            {paymentMethods.map((method) => (
                                <Box
                                    key={method}
                                    sx={{
                                        px: 1.4,
                                        py: 0.8,
                                        borderRadius: 999,
                                        backgroundColor: "rgba(255,255,255,0.08)",
                                        border: "1px solid rgba(255,255,255,0.12)",
                                        color: "#fff",
                                        fontFamily: '"Nunito Sans", sans-serif',
                                        fontSize: 13,
                                        fontWeight: 800,
                                    }}
                                >
                                    {method}
                                </Box>
                            ))}
                        </Box>

                        <FooterText>
                            Zahlung per Rechnung ist für Bestandskunden möglich. Der
                            Rechnungsbetrag ist innerhalb von 14 Tagen auszugleichen.
                        </FooterText>

                        <Box sx={{ mt: 2.2 }}>
                            <Typography
                                sx={{
                                    mb: 0.7,
                                    fontFamily: '"Nunito Sans", sans-serif',
                                    fontSize: 13,
                                    fontWeight: 900,
                                    letterSpacing: "0.12em",
                                    textTransform: "uppercase",
                                    color: colors.brown,
                                }}
                            >
                                Bankverbindung
                            </Typography>

                            <FooterText>
                                Volksbank Jade-Weser
                                <br />
                                IBAN: DE97282626734501589900
                                <br />
                                BIC: GENODEF1VAR
                            </FooterText>
                        </Box>
                    </Box>
                </Box>

                <Box
                    sx={{
                        py: 2.6,
                        borderTop: "1px solid rgba(255,255,255,0.12)",
                        display: "flex",
                        flexDirection: {
                            xs: "column",
                            sm: "row",
                        },
                        justifyContent: "space-between",
                        alignItems: {
                            xs: "flex-start",
                            sm: "center",
                        },
                        gap: 1.2,
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: '"Nunito Sans", sans-serif',
                            fontSize: 14,
                            color: "rgba(255,255,255,0.58)",
                        }}
                    >
                        © {new Date().getFullYear()} Hooksieler Lädchen. Alle Rechte
                        vorbehalten.
                    </Typography>

                    <Typography
                        sx={{
                            fontFamily: '"Nunito Sans", sans-serif',
                            fontSize: 14,
                            color: "rgba(255,255,255,0.58)",
                        }}
                    >
                        Webseite erstellt von{" "}
                        <Link
                            href="https://johannsen-its.de"
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="none"
                            sx={{
                                color: "#fff",
                                fontWeight: 800,

                                "&:hover": {
                                    color: colors.brown,
                                },
                            }}
                        >
                            Johannsen IT Solution
                        </Link>
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
}

export default Footer;